import { useCallback } from 'react';
import { Trash2, Zap, GitBranch, Play, Sparkles, CircleStop } from 'lucide-react';
import type { Node } from '@xyflow/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { WorkflowNodeData, WorkflowNodeType } from './types';
import { ScheduleBuilder } from './ScheduleBuilder';
import { DEFAULT_FRIENDLY_SCHEDULE, scheduleToCron } from './schedule-cron';
import { isWorkflowEventTriggerType } from '@pm-next/shared-types';

export interface WorkflowPropertiesPanelProps {
  selectedNode: Node | null;
  onUpdateNode?: (nodeId: string, data: Partial<WorkflowNodeData>) => void;
  onDeleteNode?: (nodeId: string) => void;
  className?: string;
}

const TRIGGER_TYPES = [
  { value: 'manual-test', label: 'Manual Test Trigger' },
  { value: 'payroll-generated', label: 'Payroll Generated' },
  { value: 'payroll-finalized', label: 'Payroll Finalized' },
  { value: 'employee-created', label: 'Employee Created' },
  { value: 'employee-updated', label: 'Employee Updated' },
  { value: 'scheduled', label: 'Scheduled' },
];

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'greaterThan', label: 'Greater than' },
  { value: 'lessThan', label: 'Less than' },
  { value: 'contains', label: 'Contains' },
  { value: 'notEquals', label: 'Does not equal' },
  { value: 'empty', label: 'Is empty' },
  { value: 'notEmpty', label: 'Is not empty' },
];

const ACTION_TYPES = [
  { value: 'send-email', label: 'Send Email' },
  { value: 'send-notification', label: 'Send Notification' },
  { value: 'create-task', label: 'Create Task' },
  { value: 'generate-report', label: 'Generate Report' },
  { value: 'data-loader', label: 'Data Loader' },
];

const DATA_SOURCES = [
  { value: 'employees', label: 'Employees' },
  { value: 'employee-count', label: 'Employee Count' },
  { value: 'departments', label: 'Departments' },
  { value: 'staff-by-department', label: 'Staff by Department' },
  { value: 'recent-departures', label: 'Recent Departures' },
  { value: 'workforce-summary', label: 'Workforce Summary' },
  { value: 'daily-attendance', label: 'Daily Attendance' },
];

const OUTPUT_STYLES = [
  { value: 'summary', label: 'Summary' },
  { value: 'detailed', label: 'Detailed' },
  { value: 'bullets', label: 'Bullet Points' },
];

function TriggerConfig({
  data,
  onUpdate,
}: {
  data: WorkflowNodeData;
  onUpdate: (updates: Partial<WorkflowNodeData>) => void;
}) {
  const config = (data.config || {}) as Record<string, string>;
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-xs">Trigger type</Label>
        <Select
          value={(config.triggerType as string) || ''}
          onValueChange={(v) => {
            const nextConfig: Record<string, unknown> = { ...data.config, triggerType: v };
            if (v === 'scheduled' && !(config.cron as string)?.trim()) {
              nextConfig.cron = scheduleToCron(DEFAULT_FRIENDLY_SCHEDULE);
            }
            if (!isWorkflowEventTriggerType(v)) {
              delete nextConfig.processingMode;
            }
            onUpdate({ config: nextConfig });
          }}
        >
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="Select trigger" />
          </SelectTrigger>
          <SelectContent>
            {TRIGGER_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {(config.triggerType as string) === 'scheduled' && (
        <ScheduleBuilder
          cron={(config.cron as string) || ''}
          onChange={(cron) => onUpdate({ config: { ...data.config, cron } })}
        />
      )}
      {isWorkflowEventTriggerType(String(config.triggerType ?? '')) && (
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            className="mt-0.5"
            checked={config.processingMode === 'batch'}
            onCheckedChange={(checked) =>
              onUpdate({
                config: {
                  ...data.config,
                  processingMode: checked === true ? 'batch' : 'per-event',
                },
              })
            }
          />
          <span>
            <span className="block text-xs font-medium">Process pending events in batch</span>
            <span className="block text-xs text-muted-foreground">
              One run for all queued events of this type, instead of one run per event.
            </span>
          </span>
        </label>
      )}
      <div className="space-y-2">
        <Label className="text-xs">Description</Label>
        <Textarea
          className="min-h-[60px] resize-none text-sm"
          placeholder="When does this trigger run?"
          value={data.description || ''}
          onChange={(e) => onUpdate({ description: e.target.value })}
        />
      </div>
    </div>
  );
}

function ConditionConfig({
  data,
  onUpdate,
}: {
  data: WorkflowNodeData;
  onUpdate: (updates: Partial<WorkflowNodeData>) => void;
}) {
  const config = (data.config || {}) as Record<string, string>;
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-xs">Field</Label>
        <Input
          className="h-9 text-sm"
          placeholder="e.g. Salary variance"
          value={(config.field as string) || ''}
          onChange={(e) => onUpdate({ config: { ...data.config, field: e.target.value } })}
        />
      </div>
      <div className="space-y-2">
        <Label className="text-xs">Rule</Label>
        <Select
          value={(config.operator as string) || ''}
          onValueChange={(v) => onUpdate({ config: { ...data.config, operator: v } })}
        >
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="Select rule" />
          </SelectTrigger>
          <SelectContent>
            {CONDITION_OPERATORS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-xs">Value</Label>
        <Input
          className="h-9 text-sm"
          placeholder="Value to compare"
          value={(config.value as string) || ''}
          onChange={(e) => onUpdate({ config: { ...data.config, value: e.target.value } })}
        />
      </div>
      <div className="space-y-2">
        <Label className="text-xs">Description</Label>
        <Textarea
          className="min-h-[50px] resize-none text-sm"
          placeholder="What does this check?"
          value={data.description || ''}
          onChange={(e) => onUpdate({ description: e.target.value })}
        />
      </div>
    </div>
  );
}

function ActionConfig({
  data,
  onUpdate,
}: {
  data: WorkflowNodeData;
  onUpdate: (updates: Partial<WorkflowNodeData>) => void;
}) {
  const config = (data.config || {}) as Record<string, string>;
  const actionType = (config.actionType as string) || '';
  const set = (key: string, value: string) =>
    onUpdate({ config: { ...data.config, [key]: value } });

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-xs">Action type</Label>
        <Select value={actionType} onValueChange={(v) => set('actionType', v)}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="Select action" />
          </SelectTrigger>
          <SelectContent>
            {ACTION_TYPES.map((a) => (
              <SelectItem key={a.value} value={a.value}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {actionType === 'send-email' && (
        <>
          <div className="space-y-2">
            <Label className="text-xs">Recipient</Label>
            <Input
              className="h-9 text-sm"
              placeholder='Email address or "manager"'
              value={(config.recipient as string) || ''}
              onChange={(e) => set('recipient', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Subject</Label>
            <Input
              className="h-9 text-sm"
              placeholder="Email subject"
              value={(config.subject as string) || ''}
              onChange={(e) => set('subject', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Message</Label>
            <Textarea
              className="min-h-[60px] resize-none text-sm"
              placeholder="Intro text. Output from prior AI/report steps is appended."
              value={(config.message as string) || ''}
              onChange={(e) => set('message', e.target.value)}
            />
          </div>
        </>
      )}

      {actionType === 'send-notification' && (
        <>
          <div className="space-y-2">
            <Label className="text-xs">Title</Label>
            <Input
              className="h-9 text-sm"
              placeholder="Notification title"
              value={(config.title as string) || ''}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Message</Label>
            <Textarea
              className="min-h-[60px] resize-none text-sm"
              placeholder="Notification body. Output from prior AI/report steps is appended."
              value={(config.message as string) || ''}
              onChange={(e) => set('message', e.target.value)}
            />
          </div>
        </>
      )}

      {actionType === 'create-task' && (
        <>
          <div className="space-y-2">
            <Label className="text-xs">Task title</Label>
            <Input
              className="h-9 text-sm"
              placeholder="What needs to be done?"
              value={(config.title as string) || ''}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Description</Label>
            <Textarea
              className="min-h-[60px] resize-none text-sm"
              placeholder="Task details"
              value={(config.message as string) || ''}
              onChange={(e) => set('message', e.target.value)}
            />
          </div>
        </>
      )}

      {actionType === 'generate-report' && (
        <>
          <div className="space-y-2">
            <Label className="text-xs">Purpose</Label>
            <Textarea
              className="min-h-[60px] resize-none text-sm"
              placeholder="What should the report cover?"
              value={(config.purpose as string) || ''}
              onChange={(e) => set('purpose', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Custom prompt (optional)</Label>
            <Textarea
              className="min-h-[60px] resize-none text-sm"
              placeholder="Override the default prompt entirely"
              value={(config.customPrompt as string) || ''}
              onChange={(e) => set('customPrompt', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Report title (optional)</Label>
            <Input
              className="h-9 text-sm"
              placeholder="Title for the report"
              value={(config.title as string) || ''}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>
        </>
      )}

      {actionType === 'data-loader' && (
        <>
          <div className="space-y-2">
            <Label className="text-xs">Data source</Label>
            <Select value={(config.source as string) || ''} onValueChange={(v) => set('source', v)}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select data source" />
              </SelectTrigger>
              <SelectContent>
                {DATA_SOURCES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Search (optional)</Label>
            <Input
              className="h-9 text-sm"
              placeholder="Filter term, e.g. a name"
              value={(config.search as string) || ''}
              onChange={(e) => set('search', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Limit (optional)</Label>
            <Input
              className="h-9 text-sm"
              type="number"
              min={1}
              placeholder="Max rows to load"
              value={(config.limit as string) || ''}
              onChange={(e) => set('limit', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Output key</Label>
            <Input
              className="h-9 text-sm font-mono"
              placeholder="data"
              value={(config.outputKey as string) || ''}
              onChange={(e) => set('outputKey', e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">
              Loaded data is stored under this key for later condition/AI steps.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function AIConfig({
  data,
  onUpdate,
}: {
  data: WorkflowNodeData;
  onUpdate: (updates: Partial<WorkflowNodeData>) => void;
}) {
  const config = (data.config || {}) as Record<string, string>;
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-xs">Purpose</Label>
        <Textarea
          className="min-h-[60px] resize-none text-sm"
          placeholder="What should the AI do?"
          value={(config.purpose as string) || ''}
          onChange={(e) => onUpdate({ config: { ...data.config, purpose: e.target.value } })}
        />
      </div>
      <div className="space-y-2">
        <Label className="text-xs">Custom prompt (optional)</Label>
        <Textarea
          className="min-h-[60px] resize-none text-sm"
          placeholder="Override the default prompt entirely"
          value={(config.customPrompt as string) || ''}
          onChange={(e) => onUpdate({ config: { ...data.config, customPrompt: e.target.value } })}
        />
      </div>
      <div className="space-y-2">
        <Label className="text-xs">Output style</Label>
        <Select
          value={(config.outputStyle as string) || ''}
          onValueChange={(v) => onUpdate({ config: { ...data.config, outputStyle: v } })}
        >
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="Select style" />
          </SelectTrigger>
          <SelectContent>
            {OUTPUT_STYLES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

const TYPE_LABELS: Record<WorkflowNodeType, string> = {
  trigger: 'Trigger',
  condition: 'Condition',
  action: 'Action',
  ai: 'AI Step',
  end: 'End',
};

const TYPE_ICONS: Record<WorkflowNodeType, typeof Zap> = {
  trigger: Zap,
  condition: GitBranch,
  action: Play,
  ai: Sparkles,
  end: CircleStop,
};

/**
 * Validate only the fields relevant to this node type. Returns missing field labels for the current type.
 * Used when the user clicks Validate so we never require "hidden" fields from other node types.
 */
export function validateNodeData(data: WorkflowNodeData): { valid: boolean; missing: string[] } {
  const missing: string[] = [];
  if (!(data.label ?? '').trim()) missing.push('Label');

  const config = (data.config ?? {}) as Record<string, unknown>;

  switch (data.type) {
    case 'trigger':
      if (!(config.triggerType as string)?.trim()) missing.push('Trigger type');
      if ((config.triggerType as string) === 'scheduled' && !(config.cron as string)?.trim()) {
        missing.push('Schedule');
      }
      break;
    case 'condition':
      if (!(config.field as string)?.trim()) missing.push('Field');
      if (!(config.operator as string)?.trim()) missing.push('Rule');
      break;
    case 'action': {
      const actionType = (config.actionType as string)?.trim();
      if (!actionType) missing.push('Action type');
      if (actionType === 'send-email' && !(config.recipient as string)?.trim()) {
        missing.push('Recipient');
      }
      if (actionType === 'generate-report' && !(config.purpose as string)?.trim()) {
        missing.push('Purpose');
      }
      if (actionType === 'data-loader' && !(config.source as string)?.trim()) {
        missing.push('Data source');
      }
      break;
    }
    case 'ai':
      if (!(config.purpose as string)?.trim()) missing.push('Purpose');
      break;
    default:
      break;
  }

  return { valid: missing.length === 0, missing };
}

/** Form body only (label + type-specific config). For use in panel or dialog. */
export function WorkflowPropertiesFormBody({
  selectedNode,
  onUpdateNode,
}: {
  selectedNode: Node;
  onUpdateNode?: (nodeId: string, data: Partial<WorkflowNodeData>) => void;
}) {
  const data = selectedNode.data as WorkflowNodeData;
  const handleUpdate = useCallback(
    (updates: Partial<WorkflowNodeData>) => {
      if (onUpdateNode) onUpdateNode(selectedNode.id, updates);
    },
    [selectedNode.id, onUpdateNode]
  );
  const NodeIcon = TYPE_ICONS[data.type];
  const typeLabel = TYPE_LABELS[data.type];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 rounded-lg border bg-card p-2">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
          <NodeIcon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-muted-foreground">{typeLabel}</p>
          <Input
            className="mt-0.5 h-8 text-sm font-medium"
            value={data.label}
            onChange={(e) => handleUpdate({ label: e.target.value })}
          />
        </div>
      </div>
      {data.type === 'trigger' && <TriggerConfig data={data} onUpdate={handleUpdate} />}
      {data.type === 'condition' && <ConditionConfig data={data} onUpdate={handleUpdate} />}
      {data.type === 'action' && <ActionConfig data={data} onUpdate={handleUpdate} />}
      {data.type === 'ai' && <AIConfig data={data} onUpdate={handleUpdate} />}
    </div>
  );
}

/** Shared form content for panel: header + scroll area with form body. */
export function WorkflowPropertiesFormContent({
  selectedNode,
  onUpdateNode,
  onDeleteNode,
  renderHeader,
}: {
  selectedNode: Node;
  onUpdateNode?: (nodeId: string, data: Partial<WorkflowNodeData>) => void;
  onDeleteNode?: (nodeId: string) => void;
  renderHeader: (props: { title: string; deleteButton: React.ReactNode }) => React.ReactNode;
}) {
  const deleteButton = onDeleteNode && (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-muted-foreground hover:text-destructive"
      onClick={() => onDeleteNode(selectedNode.id)}
      aria-label="Remove node"
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {renderHeader({ title: 'Properties', deleteButton })}
      <ScrollArea className="flex-1 max-h-[60vh] sm:max-h-none">
        <div className="space-y-4 p-4 pr-2">
          <WorkflowPropertiesFormBody selectedNode={selectedNode} onUpdateNode={onUpdateNode} />
        </div>
      </ScrollArea>
    </div>
  );
}

export function WorkflowPropertiesPanel({
  selectedNode,
  onUpdateNode,
  onDeleteNode,
  className,
}: WorkflowPropertiesPanelProps) {
  const data = selectedNode?.data as WorkflowNodeData | undefined;
  if (!selectedNode || !data) {
    return (
      <aside className={cn('flex w-[320px] shrink-0 flex-col border-l bg-muted/20', className)}>
        <div className="border-b px-4 py-3">
          <h2 className="text-sm font-medium text-foreground">Properties</h2>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-8 text-center">
          <p className="text-sm text-muted-foreground">Select a node to configure</p>
          <p className="text-xs text-muted-foreground">
            Click any step in the workflow to edit its settings
          </p>
        </div>
      </aside>
    );
  }

  return (
    <aside className={cn('flex w-[320px] shrink-0 flex-col border-l bg-muted/20', className)}>
      <WorkflowPropertiesFormContent
        selectedNode={selectedNode}
        onUpdateNode={onUpdateNode}
        onDeleteNode={onDeleteNode}
        renderHeader={({ title, deleteButton }) => (
          <div className="border-b px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-medium text-foreground">{title}</h2>
              {deleteButton}
            </div>
          </div>
        )}
      />
    </aside>
  );
}
